import { useCallback, useState } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { PublishedPostRead } from '../../types/api';
import { recordPublishedPost } from '../generation/generationApi';

export interface PublishFlow {
  /** The record created in this session; null until the user confirms. */
  post: PublishedPostRead | null;
  isSaving: boolean;
  error: string | null;
  record: () => void;
}

/**
 * The manual "I posted this" confirmation.
 *
 * The record for everything we do not publish ourselves — which is every
 * platform but LinkedIn — so this writes down a claim about the outside world
 * rather than something we observed. It changes nothing about the request: the
 * caption, the slides and the review trail stay exactly as they were, and there
 * is no way to un-record it because history is not state. A post published
 * through the API writes its own row (`source: 'linkedin_api'`), so this button
 * retires once one exists.
 *
 * `ALREADY_PUBLISHED` is not treated as an error. The request is already in the
 * state the user asked for — the record just is not in this tab's copy of it —
 * so it refetches instead of complaining, and the screen shows the stamp it
 * would have shown anyway.
 */
export function useRecordPublished(
  businessId: string,
  requestId: string,
  onStale: () => void,
): PublishFlow {
  const [post, setPost] = useState<PublishedPostRead | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const record = useCallback(() => {
    if (isSaving) return;

    setError(null);
    setIsSaving(true);

    void (async () => {
      try {
        const created = await recordPublishedPost(businessId, requestId);
        setPost(created);
        setIsSaving(false);
      } catch (caught) {
        setIsSaving(false);
        if (caught instanceof ApiError && caught.code === 'ALREADY_PUBLISHED') {
          onStale();
          return;
        }
        if (caught instanceof ApiError && caught.code === 'REQUEST_NOT_COMPLETE') {
          setError('This post is not finished, so there is nothing to mark as posted.');
          return;
        }
        setError(getErrorMessage(caught, 'That did not save. Try again.'));
      }
    })();
  }, [businessId, isSaving, onStale, requestId]);

  return { post, isSaving, error, record };
}
